import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { 
  TrendingUp, 
  Users, 
  ShoppingBag, 
  IndianRupee,
  ArrowUpRight,
  ArrowDownRight,
  Activity
} from 'lucide-react';

const Dashboard = () => {
  const { getConfig } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('weekly');

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API_URL}/users/dashboard?range=${range}`, getConfig());
        setDashboardData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [getConfig, range]);

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / (60 * 1000));
    const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) return <div className="text-center py-20 font-bold text-secondary-400">Loading dashboard...</div>;

  const getTrend = (val) => {
    if (!val) return 'up';
    return val.startsWith('-') ? 'down' : 'up';
  };

  const stats = [
    { 
      label: 'Total Revenue', 
      value: `₹${dashboardData?.totalRevenue || 0}`, 
      icon: IndianRupee, 
      change: dashboardData?.revenueChange || '0%', 
      trend: getTrend(dashboardData?.revenueChange), 
      color: 'bg-emerald-500' 
    },
    { 
      label: 'Total Orders', 
      value: dashboardData?.totalOrders || 0, 
      icon: ShoppingBag, 
      change: dashboardData?.ordersChange || '0%', 
      trend: getTrend(dashboardData?.ordersChange), 
      color: 'bg-blue-500' 
    },
    { 
      label: 'Total Customers', 
      value: dashboardData?.totalCustomers || 0, 
      icon: Users, 
      change: dashboardData?.customersChange || '0%', 
      trend: getTrend(dashboardData?.customersChange), 
      color: 'bg-orange-500' 
    },
    { 
      label: 'Total Products', 
      value: dashboardData?.totalProducts || 0, 
      icon: TrendingUp, 
      change: dashboardData?.productsChange || '0%', 
      trend: getTrend(dashboardData?.productsChange), 
      color: 'bg-primary-600' 
    },
  ];

  return (
    <div className="p-8 space-y-10 max-w-7xl mx-auto">
      <div className="flex justify-between items-end">
        <div className="space-y-2">
          <h1 className="text-3xl font-black italic text-secondary-900 uppercase">Dashboard</h1>
          <p className="text-secondary-500 font-medium">Welcome back, Admin. Here's what's happening today.</p>
        </div>
        <div className="flex items-center space-x-4 bg-white p-2 rounded-2xl border border-secondary-100 shadow-sm">
          <button 
            onClick={() => setRange('weekly')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${range === 'weekly' ? 'bg-primary-600 text-white shadow-lg shadow-primary-200' : 'text-secondary-600 hover:bg-secondary-50'}`}
          >
            Weekly
          </button>
          <button 
            onClick={() => setRange('monthly')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${range === 'monthly' ? 'bg-primary-600 text-white shadow-lg shadow-primary-200' : 'text-secondary-600 hover:bg-secondary-50'}`}
          >
            Monthly
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-3xl p-6 border border-secondary-100 shadow-sm hover:shadow-md transition-all relative overflow-hidden group">
            <div className={`absolute top-0 right-0 w-24 h-24 ${stat.color} opacity-5 rounded-full -mr-8 -mt-8 transition-transform group-hover:scale-150 duration-500`} />
            <div className="flex justify-between items-start mb-4">
              <div className={`${stat.color} p-3 rounded-2xl text-white shadow-lg`}>
                <stat.icon className="h-6 w-6" />
              </div>
              <div className={`flex items-center space-x-1 text-sm font-black ${stat.trend === 'up' ? 'text-emerald-500' : 'text-red-500'}`}>
                <span>{stat.change}</span>
                {stat.trend === 'up' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              </div>
            </div>
            <h3 className="text-secondary-400 font-black text-[10px] uppercase tracking-widest mb-1">{stat.label}</h3>
            <p className="text-2xl font-black text-secondary-900 italic">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-white rounded-[40px] p-8 border border-secondary-100 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-xl font-black italic text-secondary-900 uppercase tracking-tight">Recent Orders</h2>
            <button className="text-primary-600 font-black text-xs uppercase tracking-widest hover:underline">View All</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-secondary-100">
                  <th className="pb-4 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Order ID</th>
                  <th className="pb-4 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Customer</th>
                  <th className="pb-4 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Status</th>
                  <th className="pb-4 font-black text-secondary-400 text-[10px] uppercase tracking-widest text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-50">
                {dashboardData?.recentOrders?.map((order) => (
                  <tr key={order._id} className="hover:bg-secondary-50/50 transition-colors group">
                    <td className="py-5 font-bold text-secondary-900">#{order._id.substring(0, 8)}...</td>
                    <td className="py-5 text-secondary-500 font-medium">{order.user?.name || 'Guest'}</td>
                    <td className="py-5">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${order.isDelivered ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {order.isDelivered ? 'Delivered' : 'Processing'}
                      </span>
                    </td>
                    <td className="py-5 font-black text-secondary-900 italic text-right">₹{order.totalPrice}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-white rounded-[40px] p-8 border border-secondary-100 shadow-sm">
          <div className="flex items-center space-x-3 mb-8">
            <Activity className="h-6 w-6 text-primary-600" />
            <h2 className="text-xl font-black italic text-secondary-900 uppercase tracking-tight">Recent Activity</h2>
          </div>
          <div className="space-y-8">
            {dashboardData?.recentActivities && dashboardData.recentActivities.length > 0 ? (
              dashboardData.recentActivities.map((act, i) => (
                <div key={act.id} className="flex space-x-4 relative">
                  {i !== dashboardData.recentActivities.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-secondary-100 -mb-8" />}
                  <div className="w-8 h-8 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 border-4 border-white">
                    <div className="w-2.5 h-2.5 bg-primary-600 rounded-full" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-secondary-900">{act.title}</p>
                    <p className="text-xs text-secondary-500 leading-relaxed font-medium">{act.desc}</p>
                    <p className="text-[10px] font-black text-primary-600 uppercase tracking-tighter">
                      {formatTimeAgo(act.time)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-10 text-secondary-400 font-bold">No recent activities.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

